/**
 * WidgetInfoButton — small ⓘ icon in the widget header that opens a modal
 * with the measure's summary, caveats, and a deep-link to the relevant
 * section of `docs/architecture/data-dictionary.md`.
 *
 * Phase E1 of issue #90 v3 plan. Uses a simple custom modal rather than
 * @radix-ui/react-tooltip so we don't add a new dep just for this surface.
 */
import { useState } from 'react'
import { Info, X } from 'lucide-react'
import { getMeasureDoc } from './measure-docs'

interface Props {
  measure: string
  /** Optional widget title to show as the modal header. */
  title?: string
}

export default function WidgetInfoButton({ measure, title }: Props) {
  const [open, setOpen] = useState(false)
  const doc = getMeasureDoc(measure)

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title="What is this?"
        aria-label="Show measure documentation"
        className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800"
      >
        <Info size={13} />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {title || measure}
              </h3>
              <button
                onClick={() => setOpen(false)}
                className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">What it shows</h4>
                <p className="text-sm text-gray-700 dark:text-gray-300">{doc.summary}</p>
              </div>

              {doc.farOnly && (
                <div className="text-xs px-3 py-2 rounded bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border border-amber-200 dark:border-amber-700">
                  This is a FAR-only metric. The denominator excludes Other Transaction (OT) awards.
                </div>
              )}

              {doc.caveats && doc.caveats.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Caveats</h4>
                  <ul className="list-disc list-inside text-xs text-gray-700 dark:text-gray-300 space-y-1">
                    {doc.caveats.map((c, i) => (
                      <li key={i}>{c}</li>
                    ))}
                  </ul>
                </div>
              )}

              {doc.dictionarySection && (
                <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                  <a
                    href={`/docs/architecture/data-dictionary.md#${doc.dictionarySection}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Read full definition in data-dictionary →
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
